import { useState, type FormEvent } from 'react';
import { Button, Checkbox, Divider, Group, Modal, Radio, Select, Stack, Switch, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useInvalidateAnalyses, type AnalysisStaff } from '@/api/analyses';
import { registerReferenced, useKinds, type DataObject, type RegisterReferencedBody } from '@/api/objects';
import { objectName } from '@/features/objects/objectName';
import { useKindLabel } from '@/features/objects/useKindLabel';
import { brand } from '@/theme/theme';
import { AnalysisChangePreview, ObjectPreview } from './AttachPreview';
import { useRehearsal } from './useRehearsal';

/** Analyses that take outputs instead of a run: never launched, so still `planned` or `ready`. */
const RUNNABLE = new Set(['planned', 'ready']);

export interface RegisterReferencedModalProps {
  projectId: string;
  opened: boolean;
  onClose: () => void;
  /** The project's objects; the "replaces" picker offers those of the chosen kind. */
  existing: DataObject[];
  /** The project's analyses; any but a cancelled one can take the new object as an output. */
  analyses: AnalysisStaff[];
}

/**
 * Staff register data already finished in a bucket — an orthophoto from an ODM run, a corrected CHM, a
 * hand-made summary — in one step: the object, its `gs://` data (and processed data, then it is ready at
 * once), its CRS, and optionally an analysis it becomes an output of. "Check" rehearses the whole write
 * and shows what it would make; only then can it be committed.
 */
export function RegisterReferencedModal({ projectId, opened, onClose, existing, analyses }: RegisterReferencedModalProps) {
  const { t, i18n } = useTranslation('projects');
  const { t: tc } = useTranslation();
  const lang = i18n.language as 'cs' | 'en';
  const qc = useQueryClient();
  const invalidateAnalyses = useInvalidateAnalyses();
  const { data: kinds } = useKinds();
  const kindLabel = useKindLabel();

  const [kind, setKind] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [replaces, setReplaces] = useState<string | null>(null);
  const [uri, setUri] = useState('');
  const [hasProcessed, setHasProcessed] = useState(false);
  const [processedUri, setProcessedUri] = useState('');
  const [markReady, setMarkReady] = useState(false);
  const [crs, setCrs] = useState('');
  const [analysisId, setAnalysisId] = useState<string | null>(null);
  const [attach, setAttach] = useState<'outputs' | 'extra'>('extra');
  const [deliverable, setDeliverable] = useState(false);
  const [attachNote, setAttachNote] = useState('');

  const analysis = analyses.find((a) => a.public_id === analysisId) ?? null;
  const mode = analysis && RUNNABLE.has(analysis.state ?? '') ? attach : 'extra';
  const body: RegisterReferencedBody = {
    kind: (kind ?? '') as RegisterReferencedBody['kind'],
    name: name.trim(),
    notes,
    replaces: replaces ?? null,
    uri: uri.trim(),
    processed_uri: hasProcessed ? processedUri.trim() : '',
    crs: crs.trim(),
    mark_ready: !hasProcessed && markReady,
    analysis: analysisId,
    attach: mode,
    deliverable: mode === 'extra' && deliverable,
    attach_note: attachNote,
  };
  const r = useRehearsal((dryRun) => registerReferenced(projectId, body, dryRun), body);

  const handleClose = () => {
    setKind(null);
    setName('');
    setNotes('');
    setReplaces(null);
    setUri('');
    setHasProcessed(false);
    setProcessedUri('');
    setMarkReady(false);
    setCrs('');
    setAnalysisId(null);
    setAttach('extra');
    setDeliverable(false);
    setAttachNote('');
    r.reset();
    onClose();
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!r.preview) {
      await r.check();
      return;
    }
    const done = await r.commit();
    if (!done) return;
    void qc.invalidateQueries({ queryKey: ['objects', projectId] });
    void qc.invalidateQueries({ queryKey: ['project', projectId] });
    invalidateAnalyses(analysisId ?? undefined, projectId);
    notifications.show({ color: 'forest', message: t('referenced.done', { name: objectName(done.object) }) });
    handleClose();
  };

  const filled = !!kind && !!body.name && body.uri.startsWith('gs://') && (!hasProcessed || body.processed_uri.startsWith('gs://'));
  const sameKind = existing.filter((o) => o.kind === kind && !o.deleted);
  const attachable = analyses.filter((a) => a.state !== 'cancelled');
  const err = r.errors;

  return (
    <Modal opened={opened} onClose={handleClose} title={t('referenced.title')} size="lg">
      <form onSubmit={(e) => void handleSubmit(e)}>
        <Stack gap="md">
          <Text fz={13} c={brand.textMuted}>
            {t('referenced.hint')}
          </Text>
          <Select
            label={t('objects.kind')}
            data={(kinds ?? []).map((k) => ({ value: k.kind, label: k.label[lang] ?? k.kind }))}
            value={kind}
            onChange={(v) => {
              setKind(v);
              setReplaces(null);
            }}
            error={err.kind}
            required
            searchable
          />
          <TextInput label={t('objects.name')} description={t('referenced.nameHint')} value={name} onChange={(e) => setName(e.currentTarget.value)} maxLength={255} error={err.name} required />
          <TextInput label={t('objects.note')} description={t('referenced.notesHint')} value={notes} onChange={(e) => setNotes(e.currentTarget.value)} maxLength={500} error={err.notes} />
          {sameKind.length > 0 && (
            <Select
              label={t('objects.replaces')}
              data={[
                { value: '', label: t('objects.replacesNone') },
                ...sameKind.map((o) => ({ value: o.public_id, label: `${kindLabel(o.kind)} · ${objectName(o)}` })),
              ]}
              value={replaces ?? ''}
              onChange={(v) => setReplaces(v || null)}
              error={err.replaces}
            />
          )}

          <Divider label={t('referenced.dataSection')} labelPosition="left" />
          <TextInput
            label={t('referenced.uri')}
            description={t('referenced.uriHint')}
            placeholder="gs://bucket/path/"
            value={uri}
            onChange={(e) => setUri(e.currentTarget.value)}
            maxLength={512}
            error={err.uri}
            required
            ff="monospace"
          />
          <Switch label={t('referenced.hasProcessed')} description={t('referenced.hasProcessedHint')} checked={hasProcessed} onChange={(e) => setHasProcessed(e.currentTarget.checked)} />
          {hasProcessed ? (
            <TextInput
              label={t('referenced.processedUri')}
              placeholder="gs://bucket/run/step/"
              value={processedUri}
              onChange={(e) => setProcessedUri(e.currentTarget.value)}
              maxLength={512}
              error={err.processed_uri}
              required
              ff="monospace"
            />
          ) : (
            <Checkbox label={t('referenced.markReady')} description={t('referenced.markReadyHint')} checked={markReady} onChange={(e) => setMarkReady(e.currentTarget.checked)} error={err.mark_ready} />
          )}
          <TextInput label="CRS" description={t('referenced.crsHint')} placeholder="EPSG:5514" value={crs} onChange={(e) => setCrs(e.currentTarget.value)} maxLength={32} error={err.crs} />

          <Divider label={t('referenced.analysisSection')} labelPosition="left" />
          <Select
            label={t('referenced.analysis')}
            description={t('referenced.analysisHint')}
            placeholder={t('referenced.noAnalysis')}
            data={attachable.map((a) => ({ value: a.public_id, label: `${a.name[lang] || a.name.cs} · ${a.state}` }))}
            value={analysisId}
            onChange={setAnalysisId}
            error={err.analysis}
            clearable
            searchable
          />
          {analysis && (
            <>
              {RUNNABLE.has(analysis.state ?? '') && (
                <Radio.Group label={t('referenced.attachMode')} value={attach} onChange={(v) => setAttach(v as 'outputs' | 'extra')}>
                  <Stack gap={6} mt={4}>
                    <Radio value="outputs" label={t('referenced.modeOutputs')} description={t('referenced.modeOutputsHint')} />
                    <Radio value="extra" label={t('referenced.modeExtra')} description={t('referenced.modeExtraHint')} />
                  </Stack>
                </Radio.Group>
              )}
              {mode === 'extra' && (
                <Switch
                  label={t('referenced.deliverable')}
                  description={t(analysis.state === 'delivered' ? 'referenced.deliverableNowHint' : 'referenced.deliverableHint')}
                  checked={deliverable}
                  onChange={(e) => setDeliverable(e.currentTarget.checked)}
                  error={err.deliverable}
                />
              )}
              <TextInput label={t('referenced.attachNote')} description={t('referenced.attachNoteHint')} value={attachNote} onChange={(e) => setAttachNote(e.currentTarget.value)} maxLength={500} error={err.attach_note} />
            </>
          )}

          {r.preview && (
            <>
              <ObjectPreview object={r.preview.object} />
              {analysis && r.preview.analysis && <AnalysisChangePreview before={analysis} after={r.preview.analysis} added={[r.preview.object.public_id]} />}
            </>
          )}
          {r.general && (
            <Text c="red" role="alert">
              {r.general}
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose}>
              {tc('common.cancel')}
            </Button>
            {r.preview ? (
              <Button type="submit" loading={r.pending === 'commit'}>
                {t('referenced.commit')}
              </Button>
            ) : (
              <Button type="submit" variant="light" loading={r.pending === 'check'} disabled={!filled}>
                {t('referenced.check')}
              </Button>
            )}
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
